import { config } from '../config';
import { resolveWithTimeout } from '../utils/async';

import type {
  HasSessionSuccessResponse,
  Identity,
  LoginOptions,
} from '@schibsted/account-sdk-browser/src/identity';
import type { Monetization } from '@schibsted/account-sdk-browser/src/monetization';

let identity: Identity | undefined;
let monetization: Monetization | undefined;

export interface IdentitySnapshot {
  identity: Identity | null;
  sessionData: HasSessionSuccessResponse | null;
  isLoggedIn: boolean;
  isConnected: boolean;
  accountUrl: string;
  initialized: boolean;
}

let snapshot: IdentitySnapshot = {
  identity: null,
  sessionData: null,
  isLoggedIn: false,
  isConnected: false,
  accountUrl: '',
  initialized: false,
};

type Listener = () => void;
const listeners = new Set<Listener>();

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notify() {
  for (const listener of listeners) listener();
}

function getSnapshot(): IdentitySnapshot {
  return snapshot;
}

async function getIdentityInstance(): Promise<Identity> {
  if (!identity) {
    const { Identity } = await import('@schibsted/account-sdk-browser');
    const { clientId, env, sessionDomain } = config.schibstedAccount;

    identity = new Identity({
      clientId,
      redirectUri: `${window.location.origin}/login-callback`,
      env,
      sessionDomain,
    });

    if (config.env === 'development') {
      identity.clearCachedUserSession();
    }

    if (config.env === 'production') {
      identity.enableVarnishCookie({ domain: 'vg.no' });
    }
  }

  return identity;
}

async function getMonetizationInstance() {
  if (!monetization) {
    const { Monetization } = await import('@schibsted/account-sdk-browser');
    const { clientId, env, sessionDomain } = config.schibstedAccount;

    monetization = new Monetization({
      clientId,
      redirectUri: `${window.location.origin}/login-callback`,
      env,
      sessionDomain,
    });
  }

  return monetization;
}

let initPromise: Promise<void> | undefined;
async function init(): Promise<void> {
  if (initPromise) return initPromise;

  const bootstrap = async () => {
    const id = await getIdentityInstance();

    const onSessionChange = async (sessionData: HasSessionSuccessResponse) => {
      snapshot = {
        identity: id,
        sessionData,
        isLoggedIn: true,
        isConnected: Boolean(sessionData.result),
        accountUrl: await id.accountUrl(),
        initialized: true,
      };
      notify();
    };

    // https://user-identity-account-sdk-browser.pages.schibsted.ghe.com/Identity.html#event:sessionChange
    id.on('sessionChange', onSessionChange);

    // Trigger initial session check; errors mean logged-out / not connected
    await id.hasSession().catch(() => null);

    // If sessionChange didn't fire (logged-out path), mark as initialized
    if (!snapshot.initialized) {
      snapshot = { ...snapshot, identity: id, initialized: true };
      notify();
    }
  };

  initPromise = resolveWithTimeout({
    promise: bootstrap(),
    fallback: undefined,
    timeout: 4000,
  }).then(() => {
    if (!snapshot.initialized) {
      snapshot = { ...snapshot, identity: identity ?? null, initialized: true };
      notify();
    }
  });
  return initPromise;
}

const DEFAULT_LOGIN_SOURCE = 'login-flow';
function getLoginOptions(
  options: Partial<LoginOptions & { source?: string }> = {}
) {
  if (!options.state) {
    const origin = new URL(window.location.href);
    origin.searchParams.set(
      'utm_source',
      options.source || DEFAULT_LOGIN_SOURCE
    );
    // no need to encodeURIComponent here - it's done in the SDK
    options.state = origin.pathname + origin.search;
  }
  return options as LoginOptions;
}

async function login(
  options: Partial<LoginOptions & { source?: string }> = {}
) {
  const id = await getIdentityInstance();
  id.login(getLoginOptions(options));
}

function logout(redirectUri = window.location.href) {
  if (!identity) return;
  identity.logout(redirectUri);
}

export {
  // Initialization
  init,

  // State subscription (for useSyncExternalStore)
  subscribe,
  getSnapshot,

  // for player paywall integration and pulse tracking
  getIdentityInstance,
  getMonetizationInstance,

  // Auth actions
  login,
  logout,
  getLoginOptions,
};
